import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSession } from '@zehnmindai/embed/react';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Download,
  FileCheck2,
  FileSignature,
  Loader2,
  Lock,
  ScrollText,
  ShieldAlert,
  ShieldCheck,
  Unlock,
  Undo2,
  UploadCloud,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import LoadingSpinner from '@/shared/ui/LoadingSpinner';
import { publicRequest } from '@/shared/utils/request';
import { GUEST_AUTH_CHALLENGE, GUEST_AUTH_VERIFY, VERIFY } from '@/shared/utils/urls';
import { parseApiError, apiErrorMessage } from '@/shared/utils/apiError';
import { downloadBlob, fetchAuthedBlob, openBlob } from '@/shared/utils/authedFetch';
import { useEIMZO } from '@/hooks/useEIMZO';
import { maskNationalId } from '@/shared/utils/nationalId';

const STATUS_STYLE = {
  signed: {
    icon: CheckCircle2,
    box: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300',
  },
  partially_signed: {
    icon: Clock,
    box: 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200',
  },
  pending: {
    icon: Clock,
    box: 'bg-muted/50 border-border text-foreground',
  },
  rejected: {
    icon: XCircle,
    box: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300',
  },
  withdrawn: {
    icon: Undo2,
    box: 'bg-muted/50 border-border text-foreground',
  },
};

const fmt = (value, lang) => (value ? new Date(value).toLocaleString(lang) : '—');

async function sha256Hex(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * `/v/{code}` — what the QR on a signed document opens. Public, no portal.
 *
 * A restricted document shows only "registered — sign in to verify" until
 * the visitor is identified: a person signed in to docs (the session cookie
 * goes with every call on this origin by itself), or someone without an
 * account who proves their E-IMZO key here (a guest sign-in: docs' own
 * token, held in this page's memory only and sent as the header). The body
 * downloads additionally need read access or a signer slot, which the API
 * decides — the page just follows `downloads` in the payload.
 */
export const VerifyView = () => {
  const { t, i18n } = useTranslation(['signing', 'common']);
  const { code } = useParams();
  const session = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // A guest signer's token (memory only; a person's session is the cookie).
  const [token, setToken] = useState(null);
  // The chosen certificate is an organization member's: sign in instead.
  const [memberCertificate, setMemberCertificate] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [check, setCheck] = useState(null); // {name, hash, match}
  const eimzo = useEIMZO();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await publicRequest.get(VERIFY.replace('{code}', encodeURIComponent(code)), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(res.data);
    } catch (err) {
      const info = parseApiError(err);
      setError({ code: info.code, message: apiErrorMessage(info) });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [code, token]);

  // Read again once a person is signed in (another tab, or the sign-in
  // below): their cookie may unlock more.
  const signedIn = session.status === 'signed-in';
  useEffect(() => {
    load();
  }, [load, signedIn]);

  const handleConnect = async () => {
    try {
      await eimzo.connect();
      await eimzo.loadCertificates();
    } catch {
      toast.error(t('signing:eimzo.connectionFailed'));
    }
  };

  const handleGuestAuth = async () => {
    if (!eimzo.selectedCertificate) {
      toast.error(t('signing:eimzo.selectCertificate'));
      return;
    }
    try {
      setAuthLoading(true);
      setMemberCertificate(false);
      const challengeRes = await publicRequest.post(GUEST_AUTH_CHALLENGE);
      const { requestId, challenge } = challengeRes.data;
      const keyId = await eimzo.loadKey(eimzo.selectedCertificate);
      const pkcs7 = await eimzo.signData(challenge, keyId);
      const verifyRes = await publicRequest.post(GUEST_AUTH_VERIFY, { requestId, pkcs7 });
      const { accessToken } = verifyRes.data;
      if (accessToken) {
        setToken(accessToken);
        toast.success(t('signing:guest.authSuccess'));
      }
    } catch (err) {
      const parsed = parseApiError(err);
      if (parsed.code === 'certificate_has_account') {
        setMemberCertificate(true);
      } else {
        console.error('Guest auth error:', err);
        toast.error(parsed.status ? apiErrorMessage(parsed) : t('signing:guest.authFailed'));
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const fetchArtefact = async (kind) => {
    const path = data?.downloads?.[kind];
    if (!path) return;
    setBusy(kind);
    try {
      const blob = await fetchAuthedBlob(
        kind === 'sheet' ? `${path}?lang=${encodeURIComponent(i18n.language)}` : path,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      const stem = (data.title || code).replace(/\.[^.]+$/, '');
      if (kind === 'pkcs7') downloadBlob(blob, `${stem}.signatures.${blob.type === 'application/zip' ? 'zip' : 'p7m'}`);
      else openBlob(blob);
    } catch (err) {
      toast.error(err.detail?.message || t('signing:verification.downloadFailed'));
    } finally {
      setBusy(null);
    }
  };

  const onFilePicked = async (file) => {
    if (!file || !data?.renditionSha256) return;
    const hash = await sha256Hex(file);
    setCheck({ name: file.name, hash, match: hash === data.renditionSha256 });
  };

  const status = data?.signingStatus || 'pending';
  const style = STATUS_STYLE[status] || STATUS_STYLE.pending;
  const StatusIcon = style.icon;
  const signers = useMemo(() => data?.signers || [], [data]);
  const signedCount = useMemo(() => signers.filter((s) => s.status === 'signed').length, [signers]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <ShieldCheck className="w-4 h-4 text-brand" />
          <span className="font-medium text-foreground">{t('signing:verification.pageTitle')}</span>
          <span className="ml-auto font-mono text-xs tracking-wider">{code}</span>
        </div>

        {error && (
          <Card>
            <CardContent className="py-8 flex flex-col items-center text-center gap-3">
              <AlertCircle className="w-8 h-8 text-destructive" />
              <p className="font-semibold">
                {error.code === 'verification_not_found'
                  ? t('signing:verification.notFoundTitle')
                  : error.code === 'rate_limited'
                    ? t('signing:verification.rateLimited')
                    : error.message}
              </p>
              {error.code === 'verification_not_found' && (
                <p className="text-sm text-muted-foreground max-w-md">{t('signing:verification.notFoundHint')}</p>
              )}
            </CardContent>
          </Card>
        )}

        {data?.requiresAuth && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Lock className="w-4 h-4 text-brand" />
                {t('signing:verification.restrictedTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">{t('signing:verification.restrictedHint')}</p>
              {memberCertificate && (
                <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200" data-testid="verify-member-certificate">
                  <p>{t('signing:guest.certificateHasAccount')}</p>
                  <Button size="sm" variant="outline" className="w-full" onClick={() => session.signIn()}>
                    {t('signing:guest.signIn')}
                  </Button>
                </div>
              )}
              {!eimzo.connected ? (
                <Button className="w-full" variant="outline" onClick={handleConnect} disabled={eimzo.connecting}>
                  {eimzo.connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  {t('signing:eimzo.connect')}
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-500/10 p-2 rounded border border-green-500/20">
                    <Unlock className="w-4 h-4" />
                    {t('signing:eimzo.connected')}
                  </div>
                  <Select
                    value={eimzo.selectedCertificate?.serialNumber || ''}
                    onValueChange={(id) => eimzo.setSelectedCertificate(eimzo.certificates.find((c) => (c.serialNumber || c.id) === id))}
                  >
                    <SelectTrigger className="w-full h-auto py-2">
                      <SelectValue placeholder={t('signing:eimzo.selectCertificate')}>
                        {eimzo.selectedCertificate && <span className="font-medium text-xs">{eimzo.selectedCertificate.fullName}</span>}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {eimzo.certificates.map((cert) => (
                        <SelectItem key={cert.serialNumber || cert.id} value={cert.serialNumber || cert.id} disabled={cert.isExpired}>
                          <div className="flex flex-col min-w-0 pr-6 py-1">
                            <span className="font-medium truncate">{cert.O && !cert.O.includes('.') ? cert.O : cert.fullName}</span>
                            <span className="text-xs text-muted-foreground">{t('signing:candidates.pinfl')}: {maskNationalId(cert.PINFL)}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button className="w-full text-white" style={{ background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)' }} onClick={handleGuestAuth} disabled={authLoading || !eimzo.selectedCertificate}>
                    {authLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                    {t('signing:verification.signInToVerify')}
                  </Button>
                </div>
              )}
              <div className="text-xs text-muted-foreground flex items-start gap-2">
                <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                {t('signing:verification.restrictedFootnote')}
              </div>
            </CardContent>
          </Card>
        )}

        {data && !data.requiresAuth && (
          <>
            <div className={`rounded-lg border p-4 flex items-start gap-3 ${style.box}`}>
              <StatusIcon className="w-6 h-6 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-semibold text-base">
                  {t(`signing:verification.banner.${status}`, { signed: signedCount, total: signers.length })}
                </p>
                {data.endedAt && (
                  <p className="text-xs opacity-80" data-testid="verify-ended-at">
                    {t('signing:verification.endedAt', { when: fmt(data.endedAt, i18n.language) })}
                  </p>
                )}
                <p className="text-sm opacity-80 truncate">{data.title}</p>
                {data.organizationName && <p className="text-xs opacity-70">{data.organizationName}</p>}
              </div>
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileSignature className="w-4 h-4 text-brand" />
                  {t('signing:verification.signers')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {signers.length === 0 && (
                  <p className="text-sm text-muted-foreground">{t('signing:verification.noSigners')}</p>
                )}
                {signers.map((s) => {
                  const signed = s.status === 'signed';
                  return (
                    <div key={`${s.order}-${s.fullName}`} className={`rounded-md border p-3 text-sm ${signed ? 'bg-green-50/60 border-green-200 dark:bg-green-900/10 dark:border-green-900' : 'bg-muted/30 border-border'}`}>
                      <div className="flex items-center gap-2">
                        {signed ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" /> : s.status === 'rejected' ? <XCircle className="w-4 h-4 text-red-500 shrink-0" /> : <Clock className="w-4 h-4 text-amber-500 shrink-0" />}
                        <span className="font-medium truncate">{s.fullName || '—'}</span>
                        {s.pinflMasked && <span className="ml-auto font-mono text-xs text-muted-foreground">{s.pinflMasked}</span>}
                      </div>
                      {signed && (
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <div><span className="text-muted-foreground">{t('signing:eimzo.signedAt')}:</span> {s.tsaTime || fmt(s.signedAt, i18n.language)}</div>
                          <div>
                            <span className="text-muted-foreground">{t('signing:eimzo.timestamp')}:</span>{' '}
                            {s.isTimestamped ? t('signing:verification.timestamped') : <span className="text-amber-600">{t('signing:eimzo.noTimestamp')}</span>}
                          </div>
                          <div><span className="text-muted-foreground">{t('signing:eimzo.serialNumber')}:</span> <span className="font-mono">{s.serial || '—'}</span></div>
                          <div className="truncate" title={s.issuer || ''}><span className="text-muted-foreground">{t('signing:verification.issuer')}:</span> {s.issuer || '—'}</div>
                          <div><span className="text-muted-foreground">{t('signing:verification.validity')}:</span> {fmt(s.validFrom, i18n.language)} — {fmt(s.validTo, i18n.language)}</div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground">{t('signing:status.status')}:</span>
                            <Badge variant="outline" className={`h-5 text-[10px] ${s.verificationStatus === 'accepted' ? 'text-green-700 border-green-300' : 'text-red-700 border-red-300'}`}>
                              {t(`signing:verification.result.${s.verificationStatus || 'unknown'}`)}
                            </Badge>
                            {s.certificateStatus && s.certificateStatus !== 'valid' && s.certificateStatus !== 'unknown' && (
                              <Badge variant="outline" className="h-5 text-[10px] text-amber-700 border-amber-300">
                                {t(`signing:verification.certificate.${s.certificateStatus}`)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileCheck2 className="w-4 h-4 text-brand" />
                  {t('signing:verification.integrityTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">{t('signing:verification.integrityHint')}</p>
                <div className="text-[11px] font-mono break-all bg-muted/40 rounded p-2 border border-border">{data.renditionSha256 || '—'}</div>
                <label className="flex items-center justify-center gap-2 border border-dashed border-border rounded-md p-4 text-sm text-muted-foreground cursor-pointer hover:bg-muted/30">
                  <UploadCloud className="w-4 h-4" />
                  {t('signing:verification.dropFile')}
                  <input type="file" className="hidden" onChange={(e) => onFilePicked(e.target.files?.[0])} />
                </label>
                {check && (
                  <div className={`flex items-center gap-2 text-sm rounded-md border p-3 ${check.match ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:text-red-300'}`}>
                    {check.match ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
                    <span className="min-w-0 truncate">
                      {check.match ? t('signing:verification.fileMatches', { name: check.name, interpolation: { escapeValue: false } }) : t('signing:verification.fileDiffers', { name: check.name, interpolation: { escapeValue: false } })}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {data.downloads ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Button variant="outline" size="sm" disabled={busy === 'pdf'} onClick={() => fetchArtefact('pdf')}>
                  <FileCheck2 className="w-4 h-4 mr-2" />
                  {t('signing:verification.openRendition')}
                </Button>
                <Button variant="outline" size="sm" disabled={busy === 'pkcs7' || signedCount === 0} onClick={() => fetchArtefact('pkcs7')}>
                  <Download className="w-4 h-4 mr-2" />
                  {t('signing:verification.downloadPkcs7')}
                </Button>
                <Button variant="outline" size="sm" disabled={busy === 'sheet'} onClick={() => fetchArtefact('sheet')}>
                  <ScrollText className="w-4 h-4 mr-2" />
                  {t('signing:verification.openSheet')}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground flex items-start gap-2">
                <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                {t('signing:verification.noDownloadsHint')}
              </p>
            )}

            <p className="text-[11px] text-muted-foreground text-center pt-2">
              {t('signing:verification.footer', { when: fmt(data.lastVerifiedAt, i18n.language), interpolation: { escapeValue: false } })}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyView;
